import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChatThread } from "@/components/ChatThread";
import { AttachMenu } from "@/components/AttachMenu";
import { toast } from "@/components/ui/sonner";
import { timeAgo } from "@/lib/format";
import { MessageSquare, X, Minus, Maximize2, Send, ExternalLink, ChevronUp, Sparkles, Loader2, ClipboardList, Bot } from "lucide-react";

const Ctx = createContext(null);
export const useChatDock = () => useContext(Ctx);
const MAX_OPEN = 4;

export function ChatDockProvider({ children }) {
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [open, setOpen] = useState([]); // [{id, min}]
  const seen = useRef(null);

  const openChat = useCallback((id) => setOpen((o) => {
    if (o.some((x) => x.id === id)) return o.map((x) => (x.id === id ? { ...x, min: false } : x));
    const next = [...o, { id, min: false }];
    return next.length > MAX_OPEN ? next.slice(next.length - MAX_OPEN) : next;
  }), []);
  const closeChat = (id) => setOpen((o) => o.filter((x) => x.id !== id));
  const toggleMin = (id) => setOpen((o) => o.map((x) => (x.id === id ? { ...x, min: !x.min } : x)));

  useEffect(() => {
    if (!user) return undefined;
    const poll = async () => {
      try {
        const { data } = await api.get("/conversations?page_size=100");
        setConvs(data.items);
        const prev = seen.current;
        if (prev) {
          for (const c of data.items) {
            const before = prev[c.id];
            const isNew = before === undefined || (c.unread_count > before && c.last_message_direction === "inbound");
            if (isNew && c.unread_count > 0) { openChat(c.id); toast(`${c.display_name}: ${c.last_message_preview || "new message"}`, { icon: "💬" }); }
          }
        }
        seen.current = Object.fromEntries(data.items.map((c) => [c.id, c.unread_count]));
      } catch { /* transient poll failure; next tick retries */ }
    };
    poll();
    const t = setInterval(poll, 8000);
    return () => clearInterval(t);
  }, [user, openChat]);

  return <Ctx.Provider value={{ convs, open, openChat, closeChat, toggleMin }}>{children}</Ctx.Provider>;
}

function ChatWindow({ conv, min, onClose, onMin }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState(null);
  const [detail, setDetail] = useState(null);
  const [text, setText] = useState("");
  const [suggests, setSuggests] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [enquiry, setEnquiry] = useState(null);
  const load = useCallback(async () => {
    const [m, d] = await Promise.all([api.get(`/conversations/${conv.id}/messages`), api.get(`/conversations/${conv.id}`)]);
    setMessages(m.data); setDetail(d.data);
  }, [conv.id]);
  useEffect(() => {
    if (min) return undefined;
    load().then(() => api.post(`/conversations/${conv.id}/read`));
    const t = setInterval(load, 8000); return () => clearInterval(t);
  }, [min, load, conv.id]);
  const send = async () => {
    if (!text.trim()) return;
    try { await api.post(`/conversations/${conv.id}/messages`, { body: text }); setText(""); setSuggests([]); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const suggest = async () => {
    setAiLoading(true);
    try { const { data } = await api.post(`/conversations/${conv.id}/ai-suggest`); setSuggests(data.suggestions || []); }
    catch (e) { toast.error(apiError(e)); }
    finally { setAiLoading(false); }
  };
  const draftEnquiry = async () => {
    setAiLoading(true);
    try { const { data } = await api.post(`/conversations/${conv.id}/ai-enquiry`); setEnquiry(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setAiLoading(false); }
  };
  const runAgent = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/ai/run/${conv.id}`);
      if (data.reply) setText(data.reply);
      const n = data.items?.length || 0;
      toast.success(n ? `AI drafted a reply + ${n} item quote — review in AI Agent` : "AI drafted a reply — review it below");
    } catch (e) { toast.error(apiError(e)); }
    finally { setAiLoading(false); }
  };
  const windowOpen = detail?.window_open;

  return (
    <div className={`w-80 bg-white rounded-t-xl shadow-2xl border border-[#E6E8F0] border-b-0 flex flex-col shrink-0 ${min ? "" : "h-[26rem] max-h-[calc(100vh-7rem)]"}`} data-testid={`chat-window-${conv.phone}`}>
      <div className="h-11 px-3 flex items-center gap-2 bg-[#101D51] text-white rounded-t-xl cursor-pointer" onClick={onMin}>
        <div className="w-7 h-7 rounded-full bg-[#25D366] text-white text-xs font-bold flex items-center justify-center">{conv.display_name?.[0]}</div>
        <div className="min-w-0 flex-1"><div className="text-sm font-semibold truncate">{conv.display_name}</div>
          <div className="text-[10px] text-white/60 truncate">{windowOpen === false ? "window closed" : `+91 ${conv.phone}`}</div></div>
        {conv.unread_count > 0 && min && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#F83A84] text-[10px] font-bold flex items-center justify-center" data-testid={`chat-unread-${conv.phone}`}>{conv.unread_count}</span>}
        <button onClick={(e) => { e.stopPropagation(); navigate(`/inbox?c=${conv.id}`); }} title="Open in Inbox" data-testid={`chat-expand-${conv.phone}`}><ExternalLink className="w-4 h-4 text-white/70 hover:text-white" /></button>
        <button onClick={(e) => { e.stopPropagation(); onMin(); }} title={min ? "Expand" : "Minimise"} data-testid={`chat-min-${conv.phone}`}>{min ? <Maximize2 className="w-4 h-4 text-white/70 hover:text-white" /> : <Minus className="w-4 h-4 text-white/70 hover:text-white" />}</button>
        <button onClick={(e) => { e.stopPropagation(); onClose(); }} title="Close" data-testid={`chat-close-${conv.phone}`}><X className="w-4 h-4 text-white/70 hover:text-white" /></button>
      </div>
      {!min && (
        <>
          <div className="flex-1 overflow-y-auto">{messages ? <ChatThread messages={messages} className="min-h-full text-[13px]" /> : <div className="p-4 text-xs text-[#6B7189]">Loading…</div>}</div>
          {suggests.length > 0 && (
            <div className="border-t border-[#E6E8F0] p-2 space-y-1" data-testid={`chat-suggestions-${conv.phone}`}>
              <div className="text-[10px] uppercase tracking-wide text-[#6B7189] px-1 flex items-center gap-1"><Sparkles className="w-3 h-3 text-[#101D51]" /> AI suggested replies</div>
              {suggests.map((s, i) => (
                <button key={i} onClick={() => { setText(s); setSuggests([]); }} data-testid={`chat-suggestion-${conv.phone}-${i}`}
                  className="w-full text-left text-[12px] leading-snug bg-[#F5F6FA] hover:bg-[#E6F5F0] border border-[#E6E8F0] rounded-lg px-2.5 py-1.5 text-[#101D51] transition-colors">{s}</button>
              ))}
            </div>
          )}
          {enquiry && (
            <div className="border-t border-[#E6E8F0] p-2 space-y-1" data-testid={`chat-enquiry-${conv.phone}`}>
              <div className="text-[10px] uppercase tracking-wide text-[#6B7189] px-1 flex items-center gap-1"><ClipboardList className="w-3 h-3 text-[#F83A84]" /> AI enquiry draft · {enquiry.priority}</div>
              <div className="text-[12px] leading-snug bg-[#FDF2F7] border border-[#F8D3E4] rounded-lg px-2.5 py-1.5 text-[#101D51]">{enquiry.description}</div>
              <div className="flex gap-3 px-1">
                <button onClick={() => { navigator.clipboard.writeText(enquiry.description); toast.success("Copied"); }} className="text-[11px] font-semibold text-[#101D51] underline" data-testid={`chat-enquiry-copy-${conv.phone}`}>Copy</button>
                <button onClick={() => setEnquiry(null)} className="text-[11px] text-[#6B7189] ml-auto">Dismiss</button>
              </div>
            </div>
          )}
          <div className="border-t border-[#E6E8F0] p-2 flex gap-2 items-end">
            <AttachMenu conv={conv} disabled={windowOpen === false} onSent={load} size="h-9 w-9" testid={`chat-attach-${conv.phone}`} />
            <button onClick={suggest} disabled={aiLoading} title="Suggest replies with AI" data-testid={`chat-ai-${conv.phone}`}
              className="w-9 h-9 rounded-lg bg-[#101D51]/10 text-[#101D51] flex items-center justify-center hover:bg-[#101D51]/20 disabled:opacity-40 shrink-0 transition-colors">
              {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            </button>
            <button onClick={draftEnquiry} disabled={aiLoading} title="Draft an enquiry from this chat" data-testid={`chat-enquiry-btn-${conv.phone}`}
              className="w-9 h-9 rounded-lg bg-[#F83A84]/10 text-[#F83A84] flex items-center justify-center hover:bg-[#F83A84]/20 disabled:opacity-40 shrink-0 transition-colors">
              <ClipboardList className="w-4 h-4" />
            </button>
            <button onClick={runAgent} disabled={aiLoading} title="Run AI Sales Agent (match products + draft quote)" data-testid={`chat-agent-${conv.phone}`}
              className="w-9 h-9 rounded-lg bg-[#0B7C5A]/10 text-[#0B7C5A] flex items-center justify-center hover:bg-[#0B7C5A]/20 disabled:opacity-40 shrink-0 transition-colors">
              <Bot className="w-4 h-4" />
            </button>
            <textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} disabled={windowOpen === false} data-testid={`chat-input-${conv.phone}`}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={windowOpen === false ? "24h window closed — use Inbox templates" : "Reply…"}
              className="flex-1 resize-none rounded-lg border border-[#E6E8F0] px-3 py-2 text-sm focus:outline-none focus:border-[#101D51] max-h-24 disabled:bg-[#F5F6FA]" />
            <button onClick={send} disabled={!text.trim() || windowOpen === false} className="w-9 h-9 rounded-lg bg-[#0B7C5A] text-white flex items-center justify-center disabled:opacity-40" data-testid={`chat-send-${conv.phone}`}><Send className="w-4 h-4" /></button>
          </div>
        </>)}
    </div>
  );
}

export function ChatDock() {
  const { convs, open, openChat, closeChat, toggleMin } = useChatDock();
  const [list, setList] = useState(false);
  const [q, setQ] = useState("");
  const unread = convs.reduce((s, c) => s + (c.unread_count || 0), 0);
  const shown = convs.filter((c) => !q || (c.display_name || "").toLowerCase().includes(q.toLowerCase()) || (c.phone || "").includes(q));
  const openIds = new Set(open.map((o) => o.id));
  return (
    <div className="hidden md:block fixed bottom-0 inset-x-0 z-40 pointer-events-none" data-testid="utility-bar">
      {/* Docked chat windows: bottom-right, never under the conversation list */}
      <div className="absolute bottom-10 right-4 flex items-end gap-3 pointer-events-auto max-w-[calc(100vw-22rem)] overflow-x-auto">
        {open.map((o) => { const c = convs.find((x) => x.id === o.id); return c ? <ChatWindow key={o.id} conv={c} min={o.min} onClose={() => closeChat(o.id)} onMin={() => toggleMin(o.id)} /> : null; })}
      </div>
      {/* Conversation list: anchored above the WhatsApp button, bottom-left */}
      {list && (
        <div className="absolute bottom-12 left-4 w-80 bg-white rounded-xl shadow-2xl border border-[#E6E8F0] flex flex-col max-h-[70vh] pointer-events-auto z-50" data-testid="utility-chat-list">
          <div className="px-3 py-2 border-b border-[#E6E8F0] flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wide text-[#6B7189] flex-1">Conversations · {unread} unread</span>
            <button onClick={() => setList(false)} className="text-[#6B7189] hover:text-[#101D51]" data-testid="utility-list-close"><X className="w-4 h-4" /></button>
          </div>
          <div className="p-2 border-b border-[#F0F1F5]">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or phone…" data-testid="utility-list-search"
              className="w-full h-8 px-3 rounded-lg border border-[#E6E8F0] text-sm focus:outline-none focus:border-[#101D51]" />
          </div>
          <div className="overflow-y-auto">
            {shown.length === 0 && <div className="p-4 text-xs text-[#6B7189] text-center">No conversations</div>}
            {shown.map((c) => (
              <button key={c.id} onClick={() => { openChat(c.id); setList(false); }} data-testid={`utility-conv-${c.phone}`} className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-[#F5F6FA] border-b border-[#F0F1F5] last:border-0 ${openIds.has(c.id) ? "bg-[#F5F6FA]/70" : ""}`}>
                <div className="relative"><div className="w-8 h-8 rounded-full bg-[#101D51]/10 text-[#101D51] text-xs font-bold flex items-center justify-center">{c.display_name?.[0]}</div>
                  {openIds.has(c.id) && <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#25D366] border-2 border-white" title="Open" />}</div>
                <div className="min-w-0 flex-1"><div className="text-sm font-medium text-[#101D51] truncate">{c.display_name}</div><div className="text-[11px] text-[#6B7189] truncate">{c.last_message_direction === "outbound" ? "You: " : ""}{c.last_message_preview}</div></div>
                <div className="text-right shrink-0"><div className="text-[10px] text-[#6B7189]">{timeAgo(c.last_message_at)}</div>{c.unread_count > 0 && <span className="inline-block min-w-[18px] h-[18px] px-1 rounded-full bg-[#0B7C5A] text-white text-[10px] font-bold text-center leading-[18px]">{c.unread_count}</span>}</div>
              </button>))}
          </div>
        </div>)}
      <div className="h-10 bg-[#101D51] border-t border-white/10 flex items-center px-4 gap-3 pointer-events-auto" data-testid="utility-bar-strip">
        <button onClick={() => setList(!list)} className={`h-8 px-3 rounded-lg text-white text-sm font-medium flex items-center gap-2 ${list ? "bg-white/25" : "bg-white/10 hover:bg-white/20"}`} data-testid="utility-whatsapp-btn">
          <MessageSquare className="w-4 h-4 text-[#25D366]" /> WhatsApp
          {unread > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#F83A84] text-[10px] font-bold flex items-center justify-center" data-testid="utility-unread">{unread}</span>}
          <ChevronUp className={`w-3.5 h-3.5 transition-transform ${list ? "rotate-180" : ""}`} />
        </button>
        {/* Minimised-chat tabs live in the strip like Salesforce utility items */}
        <div className="flex items-center gap-1 overflow-x-auto">
          {open.map((o) => { const c = convs.find((x) => x.id === o.id); return c ? (
            <button key={o.id} onClick={() => toggleMin(o.id)} data-testid={`utility-tab-${c.phone}`}
              className={`h-8 px-2.5 rounded-lg text-xs font-medium flex items-center gap-1.5 whitespace-nowrap ${o.min ? "bg-white/10 text-white/80 hover:bg-white/20" : "bg-[#25D366]/20 text-white"}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#25D366]" />{c.display_name}
              {c.unread_count > 0 && <span className="min-w-[16px] h-4 px-1 rounded-full bg-[#F83A84] text-[10px] font-bold flex items-center justify-center">{c.unread_count}</span>}
            </button>) : null; })}
        </div>
        <span className="text-[11px] text-white/50 ml-auto hidden lg:inline">{open.length} chat{open.length === 1 ? "" : "s"} open · new customer messages pop up automatically</span>
      </div>
    </div>
  );
}
